import React, { FC, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import warningImg from '../../../../assets/images/warning.png';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { SafeHooks } from '../../../../hooks/index';
import { EventFormType } from '../../../../types/events.types';
import { PageRoute } from '../../../../types/page.types';
import { ChildrenFormProps, EditEventStepVariant } from '../event-form.types';
import {
  bulkStepOneSchema,
  FormSchema,
  grantItemsArraySchema,
  stepOne as stepOneFormSchema,
} from '../event-form.validation';
import {
  BulkGrantStepOne,
  BuyBackStepOne,
  ClassConversionStepOne,
  FundraisingRoundStepOne,
  GrantStepSecond,
  SecondariesStepOne,
  ShareIssuanceStepOne,
  ValuationStepOne,
} from '../step-components/index';
import { eventStorage } from './steps.store';

export type StepOneProps = {
  onEditClick?: (step: EditEventStepVariant) => void;
} & ChildrenFormProps;

export const StepOne: FC<StepOneProps> = ({
  handleCloseModal,
  nextFormStep,
  prevFormStep,
  control,
  setFormData,
  formData,
  companyId,
  issuedShares,
  lockMode,
  filedState,
  setError,
  clearErrors,
  errors,
  reset,
  event,
  isLoading: isPendingSave,
  editWizardStep,
  isGrantEditWizard,
  onEditClick,
}) => {
  const { stepOne, initialStep } = useWatch<FormSchema>({ control });
  const schemaToUse =
    initialStep?.type === EventFormType.BULK_IMPORT_GRANT ? bulkStepOneSchema : stepOneFormSchema;

  const isValid = useMemo(() => {
    if (initialStep?.type === EventFormType.GRANT) {
      return grantItemsArraySchema.safeParse(stepOne?.eventDetails?.grant?.grantItems).success;
    }
    return schemaToUse.safeParse(stepOne).success;
  }, [initialStep?.type, schemaToUse, stepOne]);

  const isSomethingLoading = eventStorage(({ isLoading }) => isLoading);
  // Uniqueness is a server verdict, so no Zod schema can carry it — gate the step on it directly
  // instead of on the RHF error, which handleSubmit clears when it re-runs the resolver.
  const isValuationNameTaken = eventStorage(({ isValuationNameTaken }) => isValuationNameTaken);
  const { isCompanyHaveSafes, isLoading } = SafeHooks.useIsCompanyHaveSafes({ companyId });
  const [showWarning, setShowWarning] = useState<boolean>(isCompanyHaveSafes === 'true');

  const navigate = useNavigate();

  const handleBack = () => {
    if (isGrantEditWizard) {
      prevFormStep();
      return;
    }
    if (
      initialStep?.type === EventFormType.GRANT ||
      initialStep?.type === EventFormType.BULK_IMPORT_GRANT
    ) {
      prevFormStep();
    } else {
      reset();
      prevFormStep();
    }
  };

  const showGrantEditWizardNav =
    Boolean(isGrantEditWizard) && initialStep?.type === EventFormType.GRANT;

  useEffect(() => {
    if (!initialStep?.type) return;
    const stepOneEventDetailTypes: EventFormType[] = [
      EventFormType.GRANT,
      EventFormType.BULK_IMPORT_GRANT,
      EventFormType.FUNDRAISING_ROUND,
      EventFormType.SHARE_ISSUANCE,
      EventFormType.SECONDARIES,
      EventFormType.BUYBACK,
      EventFormType.CONVERSION,
      EventFormType.VALUATION,
    ];
    const active = initialStep.type;
    for (const key of stepOneEventDetailTypes) {
      if (key === active) {
        setFormData(
          `stepOne.eventDetails.${key}.enabled` as `stepOne.eventDetails.grant.enabled`,
          true,
        );
      } else if (!(active === EventFormType.BULK_IMPORT_GRANT && key === EventFormType.GRANT)) {
        setFormData(`stepOne.eventDetails.${key}` as `stepOne.eventDetails.grant`, undefined);
      }
    }
  }, [initialStep?.type, setFormData]);

  useEffect(() => {
    if (!isLoading) {
      setShowWarning(isCompanyHaveSafes === 'true');
    }
  }, [isCompanyHaveSafes, isLoading]);

  if (initialStep?.type === EventFormType.SAFE_CONVERSION) return;

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-6 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        {stepOne?.eventDetails?.grant?.enabled &&
          initialStep?.type !== EventFormType.BULK_IMPORT_GRANT && (
            <GrantStepSecond
              clearErrors={clearErrors}
              companyId={companyId}
              control={control}
              errors={errors}
              event={event}
              formData={formData}
              setError={setError}
              setFormData={setFormData}
            />
          )}
        {stepOne?.eventDetails?.['bulk-import-grant']?.enabled && (
          <BulkGrantStepOne
            companyId={companyId}
            control={control}
            filedState={filedState}
            setFormData={setFormData}
          />
        )}
        {stepOne?.eventDetails?.['buyback']?.enabled && (
          <BuyBackStepOne
            clearErrors={clearErrors}
            companyId={companyId}
            control={control}
            errors={errors}
            event={event}
            formData={formData}
            setError={setError}
            setFormData={setFormData}
          />
        )}
        {stepOne?.eventDetails?.['secondaries']?.enabled && (
          <SecondariesStepOne
            clearErrors={clearErrors}
            companyId={companyId}
            control={control}
            errors={errors}
            event={event}
            formData={formData}
            setError={setError}
            setFormData={setFormData}
          />
        )}
        {stepOne?.eventDetails?.['conversion']?.enabled && (
          <ClassConversionStepOne
            companyId={companyId}
            control={control}
            setFormData={setFormData}
          />
        )}
        {stepOne?.eventDetails?.['valuation']?.enabled && (
          <ValuationStepOne
            companyId={companyId}
            control={control}
            issuedShares={issuedShares}
            lockMode={lockMode}
            setError={setError}
            setFormData={setFormData}
          />
        )}
        {stepOne?.eventDetails?.['fundraising-round']?.enabled && (
          <>
            {showWarning ? (
              <div className="w-full rounded border-[1px] border-gray-100 bg-gray-25">
                <div className="flex w-full flex-col items-center justify-center gap-6 py-10">
                  <div
                    className="rounded-full bg-white p-3"
                    style={{
                      boxShadow: '0px 0px 8px 0px #F7900921 inset',
                    }}
                  >
                    <div
                      className="centered flex size-28 items-center gap-3 rounded-full"
                      style={{
                        background:
                          'linear-gradient(180deg, rgba(253, 176, 34, 0.13) 0%, rgba(220, 104, 3, 0.13) 100%)',
                      }}
                    >
                      <div className="size-[56px] object-cover">
                        <img alt="" src={warningImg} />
                      </div>
                    </div>
                  </div>
                  <span className="max-w-[291px] text-center text-sm font-[450] text-gray-700">
                    You have active SAFEs which will impact the share price a new Equity Round.
                    Convert these before opening a new round.
                  </span>
                  <div className="flex w-full items-center justify-center gap-4">
                    <Button
                      className="w-fit text-nowrap border-[1px] border-gray-100 bg-gray-25 px-4 py-2 font-[450] text-brand-700 shadow-xs"
                      onClick={() => setShowWarning(false)}
                    >
                      Continue Anyway
                    </Button>
                    <Button
                      className="w-fit text-nowrap px-4 py-2"
                      onClick={() => navigate(PageRoute.SAFES)}
                    >
                      <AppFormattedMessage id="CONVERT_SAFE" />
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <FundraisingRoundStepOne
                companyId={companyId}
                control={control}
                event={event}
                handleCloseModal={handleCloseModal}
                issuedShares={issuedShares}
                lockMode={lockMode ?? false}
                onEditClick={onEditClick}
                setFormData={setFormData}
              />
            )}
          </>
        )}
        {stepOne?.eventDetails?.['share-issuance']?.enabled && (
          <ShareIssuanceStepOne
            companyId={companyId}
            control={control}
            issuedShares={issuedShares}
            lockMode={lockMode ?? false}
            setFormData={setFormData}
          />
        )}
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          {(!lockMode || showGrantEditWizardNav) && (
            <Button
              className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs disabled:opacity-50"
              disabled={Boolean(showGrantEditWizardNav && editWizardStep === 1)}
              onClick={handleBack}
              styleType="NONE"
              type="button"
            >
              <AppFormattedMessage id="BACK" />
            </Button>
          )}
          {(!showWarning || !stepOne?.eventDetails?.['fundraising-round']?.enabled) && (
            <Button
              className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
              disabled={!isValid || isSomethingLoading || isValuationNameTaken}
              isLoading={isPendingSave}
              onClick={nextFormStep}
              type="button"
            >
              {lockMode && !showGrantEditWizardNav ? (
                <AppFormattedMessage id="UPDATE" />
              ) : (
                <AppFormattedMessage id="NEXT" />
              )}
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
